import type { ReactElement, ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { AuthPageLoading } from "@/components/layout/AuthPageShell";
import { AuthLoadError } from "@/components/shared/AuthLoadError";
import { useAuth } from "@/hooks/useAuth";
import {
  needsCollaboratorOnboarding,
  needsOrgAcceptInvite,
  resolvePostAuthRoute,
} from "@/lib/authRouting";
import { resolveDefaultRoute } from "@/lib/permissions";

type RequireSessionProps = {
  children: ReactNode;
};

/** Guards first-time onboarding. Create-another-org is deferred — users who already
 *  belong to an org are sent to their default route. */
export function RequireSession({ children }: RequireSessionProps): ReactElement {
  const { user, isLoading, isError, refetch } = useAuth();

  if (isLoading) return <AuthPageLoading />;

  if (isError) return <AuthLoadError onRetry={refetch} />;

  if (!user) return <Navigate to="/login" replace />;
  if (user.needsPlatformInviteAccept) return <Navigate to="/accept-invite" replace />;
  if (needsOrgAcceptInvite(user)) return <Navigate to={resolvePostAuthRoute(user)} replace />;
  if (needsCollaboratorOnboarding(user)) return <>{children}</>;
  if (user.organization) return <Navigate to={resolveDefaultRoute(user)} replace />;
  if (user.canOnboard) return <>{children}</>;
  return <Navigate to="/no-org-access" replace />;
}
